<template>
  <div class="content">
    <div class="badge-gallery-wrap">
      <card class="card no-border-card" no-body>
        <div class="glad-badge-gallery">

          <header class="glad-badge-header">
            <p class="glad-eyebrow">Your achievements</p>
            <h1 class="glad-badge-title">Badges</h1>
            <p class="glad-badge-sub">
              Earned credentials from completed modules and pathways.
            </p>
          </header>

          <div v-if="loading" class="glad-loading">
            <span class="glad-spinner" aria-label="Loading badges"></span>
          </div>

          <template v-else-if="badges.length">
            <!-- Filter tabs -->
            <div class="glad-filter" role="tablist" aria-label="Badge type">
              <button
                v-for="opt in filterOptions"
                :key="opt.value"
                type="button"
                role="tab"
                :aria-selected="filter === opt.value"
                class="glad-filter__tab"
                :class="{ 'glad-filter__tab--active': filter === opt.value }"
                @click="filter = opt.value">
                {{ opt.label }}
                <span class="glad-filter__count">{{ countFor(opt.value) }}</span>
              </button>
            </div>

            <!-- Grid -->
            <div class="glad-badge-grid">
              <article
                v-for="b in filteredBadges"
                :key="b.id"
                class="glad-badge-card"
                :class="'glad-badge-card--' + (b.type || 'unknown')">

                <div class="glad-badge-medal" aria-hidden="true">
                  <!-- Layered SVG seal -->
                  <svg viewBox="0 0 100 100" class="glad-badge-medal__svg">
                    <defs>
                      <clipPath id="glad-seal-clip">
                        <circle cx="50" cy="50" r="40" />
                      </clipPath>
                    </defs>
                    <!-- Outer ring -->
                    <circle cx="50" cy="50" r="46" fill="none"
                      :stroke="b.type === 'pathway' ? 'var(--glad-ink)' : 'var(--glad-accent)'"
                      stroke-width="2" />
                    <!-- Inner disc -->
                    <circle cx="50" cy="50" r="40"
                      :fill="b.type === 'pathway' ? 'var(--glad-ink)' : 'var(--glad-accent)'" />
                    <!-- Notches around the inner disc -->
                    <g clip-path="url(#glad-seal-clip)" fill="#fff" opacity="0.18">
                      <rect x="49" y="6" width="2" height="88" />
                      <rect x="49" y="6" width="2" height="88" transform="rotate(45 50 50)" />
                      <rect x="49" y="6" width="2" height="88" transform="rotate(90 50 50)" />
                      <rect x="49" y="6" width="2" height="88" transform="rotate(135 50 50)" />
                    </g>
                    <!-- Mark (pathway = star, module = check) -->
                    <g v-if="b.type === 'pathway'" fill="#fff">
                      <polygon points="50,28 56,44 73,44 59,55 64,72 50,62 36,72 41,55 27,44 44,44" />
                    </g>
                    <g v-else fill="none" stroke="#fff" stroke-width="5" stroke-linecap="round" stroke-linejoin="round">
                      <polyline points="32,50 45,63 70,38" />
                    </g>
                  </svg>
                </div>

                <div class="glad-badge-body">
                  <span class="glad-badge-pill" :class="'glad-badge-pill--' + (b.type || 'unknown')">
                    {{ pillLabel(b.type) }}
                  </span>
                  <h3 class="glad-badge-card__title">{{ b.subject }}</h3>
                  <p v-if="b.pathway" class="glad-badge-card__path">{{ b.pathway }}</p>
                  <p class="glad-badge-card__date">Earned {{ formatDate(b.certificate_date) }}</p>
                </div>
              </article>
            </div>
          </template>

          <!-- Empty -->
          <div v-else class="glad-badge-empty">
            <div class="glad-badge-empty__medal" aria-hidden="true">
              <svg viewBox="0 0 100 100" width="64" height="64">
                <circle cx="50" cy="50" r="44" fill="none" stroke="var(--glad-line-strong)" stroke-width="2" />
                <circle cx="50" cy="50" r="36" fill="var(--glad-paper-2)" />
                <path d="M40 50 L48 58 L62 44" fill="none" stroke="var(--glad-text-muted)"
                  stroke-width="4" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </div>
            <h2 class="glad-badge-empty__title">No badges yet</h2>
            <p class="glad-badge-empty__sub">
              Complete a module to earn your first badge.
            </p>
            <router-link to="/glad/pathways" class="glad-btn">Explore pathways</router-link>
          </div>
        </div>
      </card>
    </div>
  </div>
</template>

<script>
export default {
  name: 'BadgeGallery',
  data() {
    return {
      loading: true,
      badges: [],
      filter: 'all',
      filterOptions: [
        { value: 'all',     label: 'All' },
        { value: 'module',  label: 'Module' },
        { value: 'pathway', label: 'Pathway' },
      ],
    };
  },
  computed: {
    filteredBadges() {
      if (this.filter === 'all') return this.badges;
      return this.badges.filter(b => b.type === this.filter);
    },
  },
  created() {
    this.fetchBadges();
  },
  methods: {
    fetchBadges() {
      this.loading = true;
      this.$http.get('glad/badges')
        .then(resp => {
          const list = (resp && resp.data && Array.isArray(resp.data.data)) ? resp.data.data : [];
          this.badges = list;
        })
        .catch(() => { this.badges = []; })
        .finally(() => { this.loading = false; });
    },
    countFor(type) {
      if (type === 'all') return this.badges.length;
      return this.badges.filter(b => b.type === type).length;
    },
    pillLabel(type) {
      if (type === 'module')  return 'Module';
      if (type === 'pathway') return 'Pathway';
      return 'Badge';
    },
    formatDate(d) {
      if (!d) return '';
      // Accept "YYYY-MM-DD" or full ISO. Render as "May 25, 2026".
      const parsed = new Date(d);
      if (isNaN(parsed.getTime())) return d;
      try {
        return parsed.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
      } catch (e) {
        return d;
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import '~@/assets/sass/glad-tokens.scss';

.badge-gallery-wrap { width: 100%; }
.badge-gallery-wrap ::v-deep > .card.no-border-card {
  border: 0;
  box-shadow: none;
}
.glad-badge-gallery {
  padding: 1.5rem 1.5rem 2.5rem;
  font-family: var(--glad-font-sans);
}

/* Header -------------------------------------------------------------------- */
.glad-badge-header { margin-bottom: 1.75rem; }
.glad-eyebrow {
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--glad-accent);
  display: block;
  margin: 0 0 0.45rem;
}
.glad-badge-title {
  font-family: var(--glad-font-serif);
  font-size: 1.7rem;
  font-weight: 700;
  color: var(--glad-ink);
  margin: 0 0 0.35rem;
  line-height: 1.2;
}
.glad-badge-sub {
  color: var(--glad-text-soft);
  font-size: 0.9rem;
  margin: 0;
}

/* Filter tabs --------------------------------------------------------------- */
.glad-filter {
  display: inline-flex;
  gap: 0.4rem;
  padding: 4px;
  background: var(--glad-paper-2);
  border-radius: 999px;
  margin-bottom: 1.5rem;
}
.glad-filter__tab {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.4rem 0.95rem;
  background: transparent;
  border: none;
  border-radius: 999px;
  font-family: var(--glad-font-sans);
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--glad-text-soft);
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;

  &:hover { color: var(--glad-ink); }
  &--active {
    background: var(--glad-surface);
    color: var(--glad-ink);
    box-shadow: 0 1px 3px rgba(15, 17, 21, 0.08);
  }
}
.glad-filter__count {
  font-size: 0.72rem;
  font-weight: 600;
  padding: 0.05rem 0.45rem;
  background: var(--glad-paper-2);
  border-radius: 999px;
  color: var(--glad-text-muted);

  .glad-filter__tab--active & { background: var(--glad-paper); color: var(--glad-ink); }
}

/* Grid --------------------------------------------------------------------- */
.glad-badge-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 1rem;
}

.glad-badge-card {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.1rem 1.15rem;
  background: var(--glad-surface);
  border: 1px solid var(--glad-line);
  border-radius: var(--glad-radius-lg);
  transition: border-color 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: var(--glad-line-strong);
    box-shadow: 0 6px 18px rgba(15, 17, 21, 0.06);
  }
}

.glad-badge-medal {
  flex-shrink: 0;
  width: 64px;
  height: 64px;
}
.glad-badge-medal__svg {
  display: block;
  width: 64px;
  height: 64px;
}

.glad-badge-body { flex: 1; min-width: 0; }

.glad-badge-pill {
  display: inline-block;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 0.18rem 0.55rem;
  border-radius: 999px;
  margin-bottom: 0.5rem;

  &--module  { background: rgba(180, 83, 9, 0.10);  color: var(--glad-accent); }
  &--pathway { background: rgba(15, 17, 21, 0.08);  color: var(--glad-ink); }
  &--unknown { background: var(--glad-paper-2);     color: var(--glad-text-muted); }
}

.glad-badge-card__title {
  font-family: var(--glad-font-serif);
  font-size: 1rem;
  font-weight: 600;
  color: var(--glad-ink);
  margin: 0 0 0.25rem;
  line-height: 1.3;
  // Allow titles to wrap but cap at 2 lines visually
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.glad-badge-card__path {
  font-size: 0.78rem;
  color: var(--glad-text-soft);
  margin: 0 0 0.35rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.glad-badge-card__date {
  font-size: 0.75rem;
  color: var(--glad-text-muted);
  margin: 0;
}

/* Loading + empty ---------------------------------------------------------- */
.glad-loading {
  display: flex;
  justify-content: center;
  padding: 4rem 0;
}
.glad-spinner {
  width: 36px;
  height: 36px;
  border: 3px solid var(--glad-line);
  border-top-color: var(--glad-ink);
  border-radius: 50%;
  animation: glad-spin 0.7s linear infinite;
  display: block;
}
@keyframes glad-spin { to { transform: rotate(360deg); } }

.glad-badge-empty {
  text-align: center;
  padding: 3rem 1rem 4rem;
  color: var(--glad-text-soft);
}
.glad-badge-empty__medal { display: inline-block; margin-bottom: 0.85rem; }
.glad-badge-empty__title {
  font-family: var(--glad-font-serif);
  font-size: 1.15rem;
  font-weight: 700;
  color: var(--glad-ink);
  margin: 0 0 0.35rem;
}
.glad-badge-empty__sub {
  font-size: 0.9rem;
  margin: 0 0 1.25rem;
}

/* Shared button (matches other GLAD pages) --------------------------------- */
.glad-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.55rem 1.25rem;
  background: var(--glad-ink);
  color: #fff;
  border: none;
  border-radius: var(--glad-radius);
  font-family: var(--glad-font-sans);
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  transition: opacity 0.15s ease;
  &:hover { opacity: 0.85; }
}
</style>
